<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import type { AditionalInfo } from '~/layers/forms/models'
import { CustomerTypeEnum, Form, Identification } from '~/layers/forms/models'
import { requiredIfValidator } from '~/utils/validators'

interface Props {
  modelValue: AditionalInfo
  product?: string | null
  customerType?: PropsTypesOf<typeof CustomerTypeEnum>['code'] | null
}

const props = withDefaults(defineProps<Props>(), {
  customerType: null,
  product: null,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const { RemoteConfigurationStore } = stores()

const v$ = useValidation(
  () => ({
    financialEntityName: {
      required: requiredIfValidator(model.value.financedInsurable === true),
    },
  }),
  model,
  {
    $scope: Form.aditionalInfo,
    $registerAs: Form.aditionalInfo,
  }
)

const {
  execute: fetchFinancialCompanies,
  data: financialCompanies,
  status: financialCompaniesStatus,
} = useSiteApi('/api/Auxiliares/InstitucionesFinancieras', {
  method: 'GET',
  transform(input?: SiteApiSchema<'InstitucionFinancieraModel'>[]) {
    if (input) return input ?? []
  },
  watch: false,
  immediate: false,
})

watch(
  () => model.value.financedInsurable,
  (n) => {
    if (n && !financialCompanies.value?.length) fetchFinancialCompanies()
  }
)

watch(
  () => model.value.onBusinessBehalf,
  () => {
    model.value.business = Identification.load()
  }
)
</script>

<template>
  <FormsGlobalContainer
    v-slot="{ sectionClasses }"
    :identifier="Form.aditionalInfo"
  >
    <FormsGlobalTitle
      text="Información adicional"
      icon-class="icon-[ph--info-bold]"
    />

    <template v-if="RemoteConfigurationStore?.ramoEnum === 'Auto'">
      <InputContainer class="col-span-full">
        <div class="flex items-center gap-3">
          <PvCheckbox v-model="model.financedInsurable" />
          <div class="text-sm md:text-base leading-tight">Este vehículo está financiado?</div>
        </div>
      </InputContainer>

      <InputField
        v-if="model.financedInsurable"
        label="Institución Financiera"
        :error="v$.financialEntityName?.$error"
        :error-message="v$.financialEntityName?.$errors[0]?.$message.toString()"
        :loading="financialCompaniesStatus === 'pending'"
      >
        <InputGroupRetry
          :retry="!financialCompanies?.length"
          @retry="fetchFinancialCompanies()"
        >
          <PvSelect
            v-model="model.financialEntityName"
            option-value="nombre"
            option-label="nombre"
            :options="financialCompanies ?? []"
            :disabled="!model.financedInsurable"
          />
        </InputGroupRetry>
      </InputField>
    </template>

    <InputContainer class="col-span-full">
      <div class="flex items-center gap-3">
        <PvCheckbox v-model="model.onBusinessBehalf" />
        <div class="text-sm md:text-base leading-tight">
          Este seguro va a ser emitido a nombre de su empresa?
        </div>
      </div>
    </InputContainer>
    <div
      v-if="model.onBusinessBehalf"
      class="col-span-full"
      :class="sectionClasses()"
    >
      <FormsIdentificationFields
        v-model="model.business"
        :validation-scope="Form.aditionalInfo"
        :entity-type="CustomerTypeEnum.Business.code"
      />
    </div>
  </FormsGlobalContainer>
</template>
